import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, XCircle, Users, LogIn } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CheckIn() {
  const { code } = useParams();
  const { user } = useAuth(); // null=checking, false=guest, obj=admin
  const [ticket, setTicket] = useState(null);
  const [state, setState] = useState("loading"); // loading | found | invalid
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/checkin/${code}`)
      .then(({ data }) => { setTicket(data); setState("found"); })
      .catch(() => setState("invalid"));
  }, [code]);

  const doCheckIn = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/checkin/${code}`);
      setTicket(data);
      setFlash(true);
      setTimeout(() => setFlash(false), 1200);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not check in — try again.");
      if (err.response?.status === 409) {
        const { data } = await api.get(`/checkin/${code}`).catch(() => ({ data: null }));
        if (data) setTicket(data);
      }
    } finally {
      setBusy(false);
    }
  };

  const remaining = ticket ? ticket.quantity - ticket.checked_in_count : 0;

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6" data-testid="checkin-page">
      <div className="grain-overlay" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className={`w-full max-w-md text-center border bg-black/40 backdrop-blur-xl p-10 transition-colors duration-500 ${flash ? "border-green-400/60" : "border-white/10"} ${state === "found" ? "gold-glow" : ""}`}>
        <div className="flex justify-center mb-3">
          <img src="/logo-jayatk.png" alt="Jay Atk" className="h-9 w-auto" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#8C7324] mb-8">Door Check-In</div>

        {state === "loading" && (
          <div data-testid="checkin-loading">
            <Loader2 className="w-8 h-8 text-[#D4AF37] mx-auto animate-spin" />
          </div>
        )}

        {state === "invalid" && (
          <div data-testid="checkin-invalid">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-5" />
            <h1 className="font-serif-display text-3xl text-[#FDFBF7]">Not a valid ticket</h1>
            <p className="text-[#A19D94] mt-3 text-sm">This QR code doesn't match a paid order. Check the receipt and try again.</p>
          </div>
        )}

        {state === "found" && ticket && (
          <div data-testid="checkin-ticket">
            {ticket.events.map((t, i) => (
              <div key={t} className="mb-1">
                <h1 className="font-serif-display text-2xl text-[#FDFBF7] leading-tight" data-testid={`checkin-event-${i}`}>{t}</h1>
                {ticket.date_labels[i] && <div className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94] mt-1">{ticket.date_labels[i]}</div>}
              </div>
            ))}
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#5c5850] mt-3">Order {ticket.order_ref}</div>

            <div className="flex items-center justify-center gap-2 mt-7" data-testid="checkin-progress">
              <Users className="w-4 h-4 text-[#D4AF37]" />
              <span className="font-serif-display text-3xl text-[#FDFBF7]" data-testid="checkin-count">
                {ticket.checked_in_count} of {ticket.quantity} in
              </span>
            </div>
            <div className="flex justify-center gap-2 mt-4">
              {Array.from({ length: ticket.quantity }).map((_, i) => (
                <span key={i} data-testid={`checkin-dot-${i}`}
                  className={`w-3 h-3 rounded-full border transition-colors duration-300 ${i < ticket.checked_in_count ? "bg-[#D4AF37] border-[#D4AF37]" : "border-white/25"}`} />
              ))}
            </div>

            {remaining > 0 ? (
              user ? (
                <button onClick={doCheckIn} disabled={busy} data-testid="checkin-button"
                  className="mt-9 w-full inline-flex items-center justify-center gap-3 border border-[#D4AF37] bg-[#D4AF37] text-black text-sm uppercase tracking-[0.22em] font-semibold px-8 py-5 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  {busy ? "Checking in…" : `Check In Guest (${ticket.checked_in_count + 1} of ${ticket.quantity})`}
                </button>
              ) : (
                <Link to="/admin/login" data-testid="checkin-login-note"
                  className="mt-9 w-full inline-flex items-center justify-center gap-3 border border-white/25 text-[#A19D94] text-xs uppercase tracking-[0.2em] px-8 py-4 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300">
                  <LogIn className="w-4 h-4" /> Door staff — log in to check in
                </Link>
              )
            ) : (
              <div className="mt-9 border border-green-400/40 bg-green-950/20 px-6 py-5" data-testid="checkin-complete">
                <Check className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <div className="text-green-300 text-sm uppercase tracking-[0.2em]">Whole party is in — enjoy</div>
              </div>
            )}

            {flash && <p className="text-green-400 text-xs uppercase tracking-[0.25em] mt-4" data-testid="checkin-flash">Checked in</p>}
            {error && <p className="text-red-400 text-xs mt-4" data-testid="checkin-error">{error}</p>}
          </div>
        )}
      </motion.div>
    </div>
  );
}
